/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { RichText } from '@/components/common/RichText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Send, Trash2, MessageSquarePlus, Heart, ShieldAlert, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SUGGESTED_PROMPTS_KEYS = [
  'aiPatientSugSymptoms',
  'aiPatientSugMed',
  'aiPatientSugPrevention',
  'aiPatientSugSpecialist',
];

const PatientAIPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get('/ai/sessions', { params: { kind: 'health' } });
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load AI sessions:', err);
    } finally { setLoadingSessions(false); }
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, sending]);

  const openSession = async (sid) => {
    setSessionId(sid);
    try {
      const { data } = await api.get(`/ai/sessions/${sid}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(t('errorOccurred'));
    }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput('');
    // Optimistic append
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    setSending(true);
    try {
      const { data } = await api.post('/ai/chat-patient', {
        session_id: sessionId,
        message: text,
      });
      if (!sessionId) {
        setSessionId(data.session_id);
        loadSessions();
      }
      setMessages(prev => [...prev, data.message]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred'));
      // Remove optimistic on failure
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally { setSending(false); }
  };

  const deleteSession = async (sid) => {
    if (!confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/ai/sessions/${sid}`);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) newSession();
    } catch (e) {
      toast.error(t('errorOccurred'));
    }
  };

  const suggested = useMemo(() => SUGGESTED_PROMPTS_KEYS.map(k => ({ key: k, text: t(k) })), [t]);

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]" data-testid="patient-ai-page">
      {/* Sessions sidebar */}
      <aside className="space-y-2" data-testid="patient-ai-sessions">
        <Button onClick={newSession} className="w-full justify-start gap-2" data-testid="patient-ai-new-session">
          <MessageSquarePlus className="h-4 w-4" /> {t('aiNewChat')}
        </Button>
        {loadingSessions ? (
          <><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2">{t('aiNoHistoryYet')}</p>
        ) : (
          <div className="space-y-1">
            {sessions.map(s => (
              <div
                key={s.session_id}
                className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-secondary transition-colors ${sessionId === s.session_id ? 'bg-secondary' : ''}`}
                onClick={() => openSession(s.session_id)}
                data-testid={`patient-ai-session-${s.session_id}`}
              >
                <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{s.title || t('aiUntitledSession')}</div>
                  <div className="text-[10px] text-muted-foreground">{s.count} {t('aiMessagesCount')}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                  className="opacity-0 group-hover:opacity-100 h-6 w-6 flex items-center justify-center rounded hover:bg-red-500/10 text-red-500"
                  aria-label={t('delete')}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* Chat area */}
      <div className="space-y-3" data-testid="patient-ai-chat">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Heart className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display font-semibold text-lg leading-tight">{t('aiPatientTitle')}</h1>
            <p className="text-xs text-muted-foreground">{t('aiPatientSubtitle')}</p>
          </div>
        </div>

        {/* Safety banner */}
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="patient-ai-safety-banner">
          <CardContent className="pt-4 flex items-start gap-3">
            <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs leading-relaxed">
              <strong>{t('aiPatientDisclaimerTitle')}</strong> {t('aiPatientDisclaimerBody')}
              {' '}
              <button onClick={() => navigate('/telemedicine')} className="underline text-primary font-medium" data-testid="patient-ai-goto-telemed">
                {t('aiPatientGotoTelemed')}
              </button>
            </div>
          </CardContent>
        </Card>

        <Card className="h-[62vh] flex flex-col">
          <ScrollArea className="flex-1" viewportRef={scrollRef} data-testid="patient-ai-scroll">
            <div className="p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-10" data-testid="patient-ai-empty">
                  <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold">{t('aiPatientEmptyTitle')}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{t('aiPatientEmptyDesc')}</p>
                  <div className="grid gap-2 sm:grid-cols-2 mt-5 max-w-xl mx-auto">
                    {suggested.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => send(s.text)}
                        className="text-left text-sm rounded-xl border border-border p-3 hover:bg-secondary transition"
                        data-testid={`patient-ai-suggest-${s.key}`}
                      >
                        {s.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm break-words ${
                      m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-secondary text-foreground'
                    }`}
                    data-testid={`patient-ai-msg-${m.role}`}
                  >
                    {m.role === 'user' ? m.content : <RichText text={m.content} />}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-3.5 py-2.5 bg-secondary flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <div className="border-t border-border p-3 flex items-center gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t('aiPatientPlaceholder')}
              disabled={sending}
              data-testid="patient-ai-input"
            />
            <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="patient-ai-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Bottom CTA */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Stethoscope className="h-4 w-4 text-primary" />
              <span>{t('aiPatientBookHint')}</span>
            </div>
            <Button size="sm" onClick={() => navigate('/telemedicine')} data-testid="patient-ai-book-doctor">
              {t('aiPatientBookCta')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PatientAIPage;
